import { Menu, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Flag from 'react-world-flags'
import { ChevronDown } from 'lucide-react'
import CookieManager from '../lib/CookiesManager'

interface LanguageOption {
  value: string
  label: string
  flag: string
}

const languages: LanguageOption[] = [
  { value: 'fr-BE', label: 'Fr', flag: 'be' },
  { value: 'nl-BE', label: 'Nl', flag: 'be' },
  { value: 'en-US', label: 'En', flag: 'en' },
  { value: 'es-ES', label: 'Es', flag: 'es' },
  { value: 'de-DE', label: 'De', flag: 'de' },
  { value: 'it-IT', label: 'It', flag: 'it' },
  { value: 'pt-PT', label: 'Pt', flag: 'pt' },
]

const defaultLanguage = 'fr-BE'

const getSavedLanguage = (): string => {
  if (typeof window === 'undefined') return defaultLanguage
  const savedLanguage = CookieManager.getLanguage()
  return savedLanguage || defaultLanguage
}

const getLanguageName = (code: string): string => {
  const languageNames: { [key: string]: string } = {
    fr: 'Français',
    nl: 'Nederlands',
    en: 'English',
    es: 'Español',
    de: 'Deutsch',
    it: 'Italiano',
    pt: 'Português',
  }
  const langCode = code.split('-')[0].toLowerCase()
  return languageNames[langCode] || code
}

export default function LanguageSelector() {
  const { i18n } = useTranslation()
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(
    languages.find(lng => lng.value === getSavedLanguage()) || languages[0],
  )

  useEffect(() => {
    const savedLang = getSavedLanguage()
    if (savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang)
    }
  }, [i18n])
  const handleLanguageChange = (language: LanguageOption) => {
    const cookies = document.cookie.split('; ')
    cookies.forEach(cookie => {
      if (cookie.startsWith('i18next=')) {
        const cookieName = cookie.split('=')[0]
        document.cookie = `${cookieName}=; path=/; domain=.sexagogo.be; expires=Thu, 01 Jan 1970 00:00:00 UTC;`
        document.cookie = `${cookieName}=; path=/; domain=www.sexagogo.be; expires=Thu, 01 Jan 1970 00:00:00 UTC;`
      }
    })

    i18n.changeLanguage(language.value)
    setSelectedLanguage(language)

    document.cookie = `i18next=${language.value}; path=/; domain=.sexagogo.be; expires=Fri, 31 Dec 9999 23:59:59 GMT; Secure; SameSite=Strict`

    CookieManager.setLanguage(language.value)
  }

  return (
    <Menu as='div' className='relative inline-block text-left'>
      <Menu.Button className='inline-flex h-10 items-center justify-center gap-x-1.5 rounded-md px-3 text-sm text-anthracite xl:shadow-sm'>
        <Flag
          code={selectedLanguage.value.split('-')[1].toLowerCase()}
          className='h-4 w-6 object-cover'
          alt={`${getLanguageName(selectedLanguage.value)} flag`}
        />
        <ChevronDown className='h-4 w-4 text-anthracite' aria-hidden='true' />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter='transition ease-out duration-100'
        enterFrom='transform opacity-0 scale-95'
        enterTo='transform opacity-100 scale-100'
        leave='transition ease-in duration-75'
        leaveFrom='transform opacity-100 scale-100'
        leaveTo='transform opacity-0 scale-95'
      >
        <Menu.Items className='absolute right-0 z-50 mt-2 w-24 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'>
          <div className='py-1'>
            {languages.map(language => (
              <Menu.Item key={language.value}>
                {({ active }) => (
                  <button
                    onClick={() => handleLanguageChange(language)}
                    className={`
                      ${active ? 'bg-grisclair' : ''}
                      ${
                        selectedLanguage.value === language.value
                          ? 'bg-gray-100'
                          : ''
                      }
                      flex w-full items-center justify-center px-3 py-2 text-sm text-gray-900
                    `}
                  >
                    <Flag
                      code={language.value.split('-')[1].toLowerCase()}
                      className='h-4 w-6 object-cover'
                      alt={`${getLanguageName(language.value)} flag`}
                    />
                    <span className='uppercase pl-2 font-bold'>
                      {language.label}
                    </span>
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
